import type { SubscriptionTier } from '@busswerks/shared'

export const DEV_TIER_OVERRIDE_KEY = 'busswerks-dev-tier-override'

/**
 * Header forwarded to the API when a dev tier override is active.
 * The Lambda only honors it when `ALLOW_DEV_TIER_OVERRIDE=true` is
 * set in its environment (dev/staging stacks only — prod leaves it
 * empty), so a forged header in prod is a no-op. See
 * `packages/api/src/lib/auth.ts` → `authenticateRequest`.
 */
export const DEV_TIER_OVERRIDE_HEADER = 'X-Busswerks-Dev-Tier'

/**
 * Where the dev tier override is allowed to take effect. True when
 * the app is not on the production hostname — Vite dev,
 * `dev.busswerks.io`, CloudFront previews, localhost, Capacitor
 * `https://localhost`. False on `busswerks.io` / `www.busswerks.io`.
 *
 * Hostname-based so one web bundle behaves correctly everywhere.
 */
export function isDevTierOverrideAllowed(): boolean {
  const viteDev = Boolean(
    (import.meta as unknown as { env?: { DEV?: boolean } }).env?.DEV,
  )
  if (viteDev) return true
  if (typeof window === 'undefined') return false
  const host = window.location.hostname.toLowerCase()
  if (host === 'busswerks.io') return false
  if (host === 'www.busswerks.io') return false
  return true
}

/**
 * Read the current override value from localStorage. Returns null
 * unless the hostname gate allows it AND the stored value is a
 * recognised tier. Shared between the fetch layer (`lib/api.ts`)
 * and the UI hook (`hooks/useEffectiveFeatures.ts`) so both see
 * the same source of truth.
 */
export function readDevTierOverride(): SubscriptionTier | null {
  if (!isDevTierOverrideAllowed()) return null
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(DEV_TIER_OVERRIDE_KEY)
    if (raw === 'free' || raw === 'pro' || raw === 'studio') return raw
  } catch {
    // localStorage can throw in sandboxed frames — skip silently.
  }
  return null
}
